/**
 * Push notifications platform adapter (R8.2).
 *
 * CRITICAL WEB SAFETY: @capacitor/push-notifications has NO web implementation.
 * Calling its methods on web throws "not implemented" errors and can crash the app.
 *
 * Strategy:
 *  - ALL functions check isNative() as the FIRST statement.
 *  - Plugin modules are loaded via dynamic import() — they are never even evaluated
 *    in the web bundle unless we're on a native platform.
 *  - Type-only imports are safe on all platforms (erased at compile time).
 */

import { Capacitor } from '@capacitor/core';
import { isNative } from './isNative';
import type { PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import type { PluginListenerHandle } from '@capacitor/core';

// ─── Re-export types used by consumers ───────────────────────────────────────

export type { PushNotificationSchema, ActionPerformed };

export type PushPermissionStatus =
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'prompt-with-rationale'
  | 'web-not-supported';

export type PushPlatform = 'ANDROID' | 'IOS';

// ─── Private dynamic loader (never runs on web) ───────────────────────────────

async function loadPush() {
  const m = await import('@capacitor/push-notifications');
  return m.PushNotifications;
}

async function loadDevice() {
  const m = await import('@capacitor/device');
  return m.Device;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Request push notification permission from the OS.
 * Returns 'web-not-supported' immediately on web — never shows a dialog.
 */
export async function requestPushPermission(): Promise<PushPermissionStatus> {
  if (!isNative()) return 'web-not-supported';
  const Push = await loadPush();
  const result = await Push.requestPermissions();
  return result.receive;
}

/**
 * Register with the OS push service (APNs / FCM) and return the registration token.
 * Must be called after permission is granted.
 *
 * @throws if called on web or if the OS registration fails.
 */
export function registerForPush(): Promise<string> {
  if (!isNative()) {
    return Promise.reject(new Error('Push not available on web'));
  }

  return new Promise<string>((resolve, reject) => {
    loadPush().then((Push) => {
      // Capacitor does not provide one-shot listeners; store handles for cleanup.
      let regHandle: PluginListenerHandle | null = null;
      let errHandle: PluginListenerHandle | null = null;

      const cleanup = () => {
        regHandle?.remove();
        errHandle?.remove();
      };

      Push.addListener('registration', (token) => {
        cleanup();
        resolve(token.value);
      }).then((h) => {
        regHandle = h;
      });

      Push.addListener('registrationError', (err) => {
        cleanup();
        reject(new Error(err.error));
      }).then((h) => {
        errHandle = h;
      });

      Push.register().catch((err) => {
        cleanup();
        reject(err);
      });
    }).catch(reject);
  });
}

/**
 * Add listeners for foreground notifications and notification tap actions.
 * Returns an async cleanup function — call it in useEffect cleanup.
 *
 * Safe no-op on web: returns a cleanup that does nothing.
 */
export async function addPushListeners(handlers: {
  onReceived?: (notification: PushNotificationSchema) => void;
  onActionPerformed?: (action: ActionPerformed) => void;
}): Promise<() => void> {
  if (!isNative()) return () => {};

  const Push = await loadPush();
  const handles: PluginListenerHandle[] = [];

  if (handlers.onReceived) {
    const h = await Push.addListener('pushNotificationReceived', handlers.onReceived);
    handles.push(h);
  }
  if (handlers.onActionPerformed) {
    const h = await Push.addListener('pushNotificationActionPerformed', handlers.onActionPerformed);
    handles.push(h);
  }

  return () => {
    handles.forEach((h) => h.remove());
  };
}

/**
 * Remove all delivered (visible) notifications from the notification centre.
 * No-op on web. Con timeout: senza google-services.json il plugin può non risolvere.
 */
export async function clearDeliveredNotifications(): Promise<void> {
  if (!isNative()) return;
  const Push = await loadPush();
  await Promise.race([
    Push.removeAllDeliveredNotifications(),
    new Promise<void>((resolve) => {
      setTimeout(resolve, 1500);
    }),
  ]);
}

/**
 * Returns the stable hardware/OS identifier for the current device.
 * On web, returns a stable UUID generated from crypto (not persisted — web callers
 * should not use this for anything meaningful since push is disabled on web anyway).
 */
export async function getDeviceId(): Promise<string> {
  if (!isNative()) return 'web-' + crypto.randomUUID();
  const Dev = await loadDevice();
  const info = await Dev.getId();
  return info.identifier;
}

/**
 * Returns the platform string accepted by the backend API.
 */
export function getPushPlatform(): PushPlatform {
  return Capacitor.getPlatform() === 'ios' ? 'IOS' : 'ANDROID';
}
